import { computed, type MaybeRef, unref } from "vue";

import type {
  components as OpenAPI,
  operations as OpenAPIOperations,
} from "@/autogen/openapi";
import { ok } from "@/lib/openapi";

import { createOrderingFromValues } from "@/lib/composables/ordering";

const SUBSCRIBER_ORDERING_VALUES = [
  "churn_date",
  "-churn_date",
  "click_rate",
  "-click_rate",
  "creation_date",
  "-creation_date",
  "email_address",
  "-email_address",
  "last_click_date",
  "-last_click_date",
  "last_open_date",
  "-last_open_date",
  "open_rate",
  "-open_rate",
  "open_date",
  "-open_date",
  "referrer_url",
  "-referrer_url",
  "risk_score",
  "-risk_score",
  "subscriber_type",
  "-subscriber_type",
  "tags",
  "-tags",
  "undeliverability_date",
  "-undeliverability_date",
  "undeliverability_reason",
  "-undeliverability_reason",
  "unsubscription_date",
  "-unsubscription_date",
  "unsubscription_reason",
  "-unsubscription_reason",
  "upgrade_date",
  "-upgrade_date",
  "utm_campaign",
  "-utm_campaign",
  "utm_medium",
  "-utm_medium",
  "utm_source",
  "-utm_source",
] as const;
export const subscriberOrdering = createOrderingFromValues(
  [...SUBSCRIBER_ORDERING_VALUES],
  { dynamicPrefixes: ["metadata:"] }
);

import api from "../../base";
import { useGenericAggregateQuery, useGenericListingQuery } from "../generic";
import { useNewsletterId } from "../utils";

export type SubscriberPage = OpenAPI["schemas"]["SubscriberPage"];

export type SubscriberFilterParameters = Omit<
  OpenAPIOperations["list_subscribers"]["parameters"]["query"],
  "ids" | "expand" | "-tag" | "ordering" | "-type" | "-domain"
> & {
  [x: `survey['${string}']`]: string[] | undefined;
};

type SubscriberOrdering = NonNullable<
  OpenAPIOperations["list_subscribers"]["parameters"]["query"]["ordering"]
>;

export const useSubscribersListingQuery = ({
  parameters,
  ordering = "-creation_date",
  maintainData = false,
}: {
  parameters?: MaybeRef<SubscriberFilterParameters | undefined>;
  ordering?: MaybeRef<SubscriberOrdering>;
  maintainData?: boolean;
} = {}) => {
  const newsletterId = useNewsletterId();

  const query = useGenericListingQuery({
    queryKey: [
      newsletterId,
      "subscribers",
      "list",
      {
        ordering,
        parameters: computed(() => {
          return unref(parameters) ?? {};
        }),
      },
    ],
    maintainData: maintainData,
    async fetch({ page, signal }) {
      const data = await ok(
        api.get("/subscribers", {
          signal,
          params: {
            query: {
              ...unref(parameters),
              ordering: unref(ordering),
              page: page,
            },
          },
        })
      );

      return data;
    },
  });

  return query;
};

export const useSubscribersAggregatesQuery = ({
  parameters,
}: {
  parameters?: MaybeRef<SubscriberFilterParameters>;
} = {}) => {
  const newsletterId = useNewsletterId();

  const query = useGenericAggregateQuery<SubscriberFilterParameters>({
    queryKey: [
      newsletterId,
      "subscribers",
      "aggregates",
      {
        parameters: computed(() => {
          return unref(parameters) ?? {};
        }),
      },
    ],
    resource: "subscriber",
    parameters,
  });

  return query;
};
